<template>
    <div>
        <Main title="Activations Report" subtitle="Signal Provider Report">
            <template v-slot:table>
                <v-app>
                    <div class="row mb-1">
                        <div class="col-md-4 align-self-center">
                            <div class="input-group">
                                <input type="text" class="form-control" v-model="search.q" placeholder="Search by activation name">
                            </div>
                        </div>
                        <div class="col-md-3 align-self-center">
                            <div class="row">
                                <div class="col-6 text-center mb-6">
                                    <v-tooltip color="primary">
                                        <template v-slot:activator="{ on, attrs }">
                                            <div
                                                v-bind="attrs"
                                                v-on="on"
                                            >
                                                <span class="info-label d-block mb-1 font-lg text-uppercase">EIDs</span>
                                                <span class="info-data p-2">{{ new Intl.NumberFormat().format(totals.eids) }}</span>
                                            </div>
                                        </template>
                                        <span>Total EIDs for the selected period.</span>
                                    </v-tooltip>
                                </div>

                                <div class="col-6 text-center mb-6">
                                    <v-tooltip color="primary">
                                        <template v-slot:activator="{ on, attrs }">
                                            <div
                                                v-bind="attrs"
                                                v-on="on"
                                            >
                                                <span class="info-label d-block mb-1 font-lg text-uppercase">Costs</span>
                                                <span class="info-data p-2">${{ new Intl.NumberFormat().format(totals.price) }}</span>
                                            </div>
                                        </template>
                                        <span>Total Costs ($)</span>
                                    </v-tooltip>
                                </div>
                            </div>
                        </div>
                        <div class="col-md-4 align-self-center">
                            <div class="form-inline">
                                <v-menu
                                    v-model="startDateMenu"
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="search.startDate"
                                            label="Start Date"
                                            clearable
                                            v-bind="attrs"
                                            v-on="on"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        header-color="#fb7e42"
                                        color="#fb7e42"
                                        v-model="search.startDate"
                                        @input="startDateMenu = false"
                                        :max="search.endDate"
                                    ></v-date-picker>
                                </v-menu>
                                <span class="mx-2">to</span>
                                <v-menu
                                    v-model="endDateMenu"
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    transition="scale-transition"
                                    offset-y
                                    min-width="auto"
                                >
                                    <template v-slot:activator="{ on, attrs }">
                                        <v-text-field
                                            v-model="search.endDate"
                                            label="End Date"
                                            clearable
                                            v-bind="attrs"
                                            v-on="on"
                                        ></v-text-field>
                                    </template>
                                    <v-date-picker
                                        header-color="#fb7e42"
                                        color="#fb7e42"
                                        v-model="search.endDate"
                                        @input="endDateMenu = false"
                                        :min="search.startDate"
                                    ></v-date-picker>
                                </v-menu>
                                <button @click.prevent="fetchData" type="button" class="btn btn-light ml-2">
                                    <i class="fas fa-sync-alt"></i>
                                </button>
                            </div>
                        </div>
                        <div class="col-1 align-self-center">
                            <div class="btn-toolbar float-right" role="toolbar">
                                <button class="btn btn-light mx-1" type="button" id="export-to-scv" @click.prevent="exportAsCsv" data-toggle="tooltip" data-placement="top" title="Export data as .csv">
                                    <i class="fas fa-file-excel"></i>
                                </button>
                                <button class="btn btn-light mx-1" type="button" data-toggle="modal" data-target=".filter-modal">
                                    <i class="fas fa-filter"></i>
                                </button>
                            </div><!--btn-toolbar-->
                        </div><!--col-->
                    </div>
                    <div class="modal fade filter-modal" tabindex="-1" role="dialog" aria-hidden="true">
                        <div class="modal-dialog modal-lg">
                            <div class="modal-content">
                                <div class="modal-header">
                                    <h5 class="modal-title">Filter Activations</h5>
                                    <button type="button" class="close" data-dismiss="modal" aria-label="Close">
                                        <span aria-hidden="true">&times;</span>
                                    </button>
                                </div>
                                <div class="modal-body">
                                    <v-row class="mt-2">
                                        <v-col
                                            cols="12"
                                            md="6"
                                        >
                                            <v-select
                                                v-model="search.sortBy"
                                                :items="sorts"
                                                label="Sort By"
                                            ></v-select>
                                        </v-col>
                                        <v-col
                                            cols="12"
                                            md="6"
                                        >
                                            <v-select
                                                v-model="search.orderBy"
                                                :items="orders"
                                                label="Order By"
                                            ></v-select>
                                        </v-col>
                                    </v-row>
                                </div>
                                <div class="modal-footer">
                                    <div class="col-md-2 col-2 px-0 text-right">
                                        <v-progress-circular
                                            v-if="search.loading"
                                            :width="2"
                                            :size="30"
                                            indeterminate
                                            color="#fb7e42"
                                            class="mr-2"
                                        ></v-progress-circular>

                                        <button type="button" class="btn btn-light" data-dismiss="modal" aria-label="Close">
                                            <span aria-hidden="true">Close</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <TableLoading cols="8" v-if="loading"></TableLoading>
                    <div class="table-responsive mt-4" v-if="!loading">
                        <table class="table">
                            <thead>
                            <tr>
                                <th>
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on">ID</span>
                                        </template>
                                        <span>Activation Identifier</span>
                                    </v-tooltip>
                                </th>
                                <th>
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on">Activation Name</span>
                                        </template>
                                        <span>Activation Name</span>
                                    </v-tooltip>
                                </th>
                                <th>
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on">Activation Date</span>
                                        </template>
                                        <span>Activation Created at</span>
                                    </v-tooltip>
                                </th>
                                <th>
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on">Activated By</span>
                                        </template>
                                        <span>Activation Created by Company</span>
                                    </v-tooltip>
                                </th>
                                <th>
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on">BID</span>
                                        </template>
                                        <span>Signal Provider BID</span>
                                    </v-tooltip>
                                </th>
                                <th>
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on">Total Sent</span>
                                        </template>
                                        <span>Total Signals Sent for Decryption</span>
                                    </v-tooltip>
                                </th>
                                <th>
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on">Total Decrypted</span>
                                        </template>
                                        <span>Total Decrypted Signals</span>
                                    </v-tooltip>
                                </th>
                                <th>
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on">Decrypted (%)</span>
                                        </template>
                                        <span>% of Decrypted signals vs Total Sent</span>
                                    </v-tooltip>
                                </th>
                                <th>
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on }">
                                            <span v-on="on">Costs ($)</span>
                                        </template>
                                        <span>Total Cost for the Decrypted signals</span>
                                    </v-tooltip>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-for="(activation, index) in activations" :key="index" class="activation-row">
                                <td>{{ activation.id || '' }}</td>
                                <td>{{ activation.name || '' }}</td>
                                <td>{{ activation.created_at || '' }}</td>
                                <td>{{ activation.company || '' }}</td>
                                <td>
                                    <ul class="list-unstyled">
                                        <li v-for="(item, itemIndex) in activation.items" :class="itemIndex !== 0 ? 'mt-1' : ''" :style="itemIndex !== 0 ? 'border-top: 1px dashed #c8ced3;' : ''">{{ item.provider }}</li>
                                    </ul>
                                </td>
                                <td>
                                    <ul class="list-unstyled">
                                        <li v-for="(item, itemIndex) in activation.items" :class="itemIndex !== 0 ? 'mt-1' : ''" :style="itemIndex !== 0 ? 'border-top: 1px dashed #c8ced3;' : ''">{{ item.eids_sent }} EIDs</li>
                                        <li v-if="activation.totals && (activation.items.length > 1)" class="font-weight-bold" style="border-top: 1px solid #000;">{{ new Intl.NumberFormat().format(activation.totals.eids) }} EIDs</li>
                                    </ul>
                                </td>
                                <td>
                                    <ul class="list-unstyled">
                                        <li v-for="(item, itemIndex) in activation.items" :class="itemIndex !== 0 ? 'mt-1' : ''" :style="itemIndex !== 0 ? 'border-top: 1px dashed #c8ced3;' : ''">{{ item.eids_decrypted }} MAIDs</li>
                                        <li v-if="activation.totals && (activation.items.length > 1)" class="font-weight-bold" style="border-top: 1px solid #000;">{{ new Intl.NumberFormat().format(activation.totals.maids) }} MAIDs</li>
                                    </ul>
                                </td>
                                <td>
                                    <ul class="list-unstyled">
                                        <li v-for="(item, itemIndex) in activation.items" :class="itemIndex !== 0 ? 'mt-1' : ''" :style="itemIndex !== 0 ? 'border-top: 1px dashed #c8ced3;' : ''">{{ item.decrypted_pct }}%</li>
                                        <li v-if="activation.totals && (activation.items.length > 1)" class="font-weight-bold" style="border-top: 1px solid #000;">{{ new Intl.NumberFormat('en-US', { maximumSignificantDigits: 4 }).format(activation.totals.maids/activation.totals.eids*100) }}%</li>
                                    </ul>
                                </td>
                                <td>
                                    <ul class="list-unstyled">
                                        <li v-for="(item, itemIndex) in activation.items" :class="itemIndex !== 0 ? 'mt-1' : ''" :style="itemIndex !== 0 ? 'border-top: 1px dashed #c8ced3;' : ''">
                                            ${{ item.costs }}
                                            <v-tooltip left v-if="item.costs">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon v-bind="attrs" v-on="on" small class="mt-n1 text-info">
                                                        mdi-information-outline
                                                    </v-icon>
                                                </template>
                                                <span>
                                            {{ item.cost_info }}<br />
                                        </span>
                                            </v-tooltip>
                                        </li>
                                        <li v-if="activation.totals && (activation.items.length > 1)" class="font-weight-bold" style="border-top: 1px solid #000;">${{ new Intl.NumberFormat('en-US', { maximumSignificantDigits: 5 }).format(activation.totals.maids * activation.fee) }}</li>
                                    </ul>
                                </td>
                            </tr>
                            </tbody>
                        </table>

                    </div>
                </v-app>
            </template>
        </Main>
    </div>
</template>

<script>
import Main from '../../components/Main'
import api from "../../api/analyses/activations";
import mixins from "../../mixin";
import TableLoading from "../../components/general/TableLoading";

export default {
    name: "ActivationReport",
    mixins: [mixins],
    data: () => ({
        filter: false,
        startDateMenu: false,
        endDateMenu: false,
        totals: {
            eids: 0,
            price: 0,
        },
        search: {
            q: '',
            sortBy: 'created_at',
            orderBy: 'desc',
            loading: false,
            startDate: '',
            endDate: '',
        },
        exportRoute: '',
        sorts: [
            {
                value: 'description',
                text: 'Name'
            },
            {
                value: 'created_at',
                text: 'Last Created'
            }
        ],
        loading: true,
        activations: [],
        activation: [],
    }),
    components: {
        TableLoading,
        Main
    },
    methods: {
        async fetchData() {
            await api.getReportActivations({
                q: this.search.q,
                sortBy: this.search.sortBy,
                orderBy: this.search.orderBy,
                startDate: this.search.startDate,
                endDate: this.search.endDate,
            })
            .then(res => {
                this.activations = res.data.data
                this.totals.eids = res.data.totals.total_eids
                this.totals.price = res.data.totals.total_price
                this.exportRoute = res.data.export_url
                this.loading = false
            })
            .catch(e => {
                this.$toast.open({
                    message: e.response.data.message,
                    type: "error",
                })
            })
        },
        exportAsCsv() {
            window.open(this.exportRoute + `?sortBy=${this.search.sortBy}&orderBy=${this.search.orderBy}&q=${this.search.q}&startDate=${this.search.startDate}&endDate=${this.search.endDate}`)
        },
        filterData: _.debounce(function() {
            this.fetchData()
            this.search.loading = false
        }, 800),
    },
    created() {
        let firstDate = new Date();
        firstDate.setMonth(firstDate.getMonth() - 1);
        firstDate.setDate(1);

        let secondDate = new Date();

        this.search.startDate = firstDate.toJSON().slice(0, 10)
        this.search.endDate = secondDate.toJSON().slice(0, 10)

        this.fetchData()
    },
    watch: {
        'search.q': function () {
            this.filterData()
        },
        'search.sortBy': function () {
            this.search.loading = true
            this.filterData()
        },
        'search.orderBy': function () {
            this.search.loading = true
            this.filterData()
        },
    },
    mounted() {

    }
}
</script>

<style scoped lang="scss">
::v-deep .v-application--wrap {
    min-height: fit-content;
}

button:focus {
    outline:0;
}

.info-data {
    border: 2px solid #fb7e42;
    border-radius: 5px;
    color: #fb7e42;
    font-size: 1.25rem;
    font-weight: bold;
}

.info-label {
    color: #fb7e42;
}

</style>